import { getAuth0Config } from '../constants';
import { useAuthStore } from '../stores';

/**
 * Clears the application-level authentication state (Zustand auth store).
 *
 * Resets the selected/pending organization IDs and the list of user
 * organizations so the app no longer holds any organization context.
 *
 * IMPORTANT: do NOT call this right before `useAuth0().logout()`. The
 * Zustand auth store is persisted to `localStorage`, so mutating it
 * (which sets `selectedOrgId = null`) triggers a re-render of the auth
 * validation logic (see `useAuthOrganizationValidation`) *while* the
 * logout redirect to `/v2/logout` is still in flight. That validator
 * then fails to refresh the token for the previous organization and
 * triggers a *second* `logout()`, producing two competing `/v2/logout`
 * navigations that the browser cancels — leaving the session
 * half-terminated (the "logout only logs out the organization" bug).
 *
 * Instead, let the SDK terminate the session and reload the page on
 * `returnTo`; call this on boot when the user turns out not to be
 * authenticated (see the startup guard in `BaseLayout`).
 */
export const clearAppAuthState = (): void => {
    useAuthStore.getState().clearSelectedOrgId();
    useAuthStore.getState().clearPendingOrgId();
    useAuthStore.getState().clearUserOrganizations();
};

/**
 * Builds the `LogoutOptions` for a full Auth0 logout that terminates
 * both the central Auth0 SSO session and the upstream Identity Provider
 * session, ensuring the user must authenticate again from scratch.
 *
 * - `clientId` is passed explicitly so the `/v2/logout` endpoint
 *   terminates the application client session regardless of the SDK
 *   cache state.
 * - `federated: true` also logs the user out of the upstream IdP.
 * - `returnTo` must be listed in the tenant's "Allowed Logout URLs"
 *   (matched exactly, without a trailing slash).
 *
 * @returns The options object to pass to `useAuth0().logout()`.
 */
export const buildFullLogoutOptions = (): {
    clientId: string;
    logoutParams: { returnTo: string; federated: true };
} => {
    const { clientId } = getAuth0Config();
    return {
        clientId,
        logoutParams: {
            returnTo: window.location.origin,
            federated: true,
        },
    };
};
